import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Users, CreditCard, BookOpen, TrendingUp, UserPlus, CheckCircle, AlertCircle, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CARD_ACCENTS = ["#EAFF00", "#FF3636", "#0A7AFF", "#22C55E"];

const recentActivities = [
  { text: "New admission application from Riya Patel", time: "2 min ago", icon: UserPlus, accent: "#FF3636" },
  { text: "Fee payment received — ₹48,000 (Sem 3)", time: "18 min ago", icon: CreditCard, accent: "#22C55E" },
  { text: "OBE report submitted by Dr. Mehta", time: "1 hr ago", icon: CheckCircle, accent: "#0A7AFF" },
  { text: "Leave request pending from 3 faculty", time: "2 hr ago", icon: AlertCircle, accent: "#F59E0B" },
  { text: "New company registered — Infosys", time: "3 hr ago", icon: TrendingUp, accent: "#EAFF00" },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0C0C10] border border-[#252530] px-3 py-2">
        <p className="font-['Syne'] font-bold text-[10px] uppercase tracking-widest text-[#EAFF00] mb-1">{label}</p>
        <p className="font-['JetBrains_Mono'] text-sm text-[#EEEADF]">{payload[0].value}</p>
      </div>
    );
  }
  return null;
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { withCredentials: true })
      .then(r => setStats(r.data)).catch(() => {});
  }, []);

  const enrollmentData = stats?.charts?.enrollment || [
    { month: "Aug", students: 320 }, { month: "Sep", students: 480 },
    { month: "Oct", students: 520 }, { month: "Nov", students: 500 },
    { month: "Dec", students: 460 }, { month: "Jan", students: 540 }, { month: "Feb", students: 580 }
  ];

  const feesData = stats?.charts?.fees || [
    { month: "Aug", collected: 8.2 }, { month: "Sep", collected: 12.4 },
    { month: "Oct", collected: 9.8 }, { month: "Nov", collected: 11.2 },
    { month: "Dec", collected: 7.6 }, { month: "Jan", collected: 13.1 }, { month: "Feb", collected: 10.5 }
  ];

  const statCards = [
    { label: "Total Students", value: stats?.stats?.total_students?.toLocaleString("en-IN") || "2,847", sub: "+12% this month" },
    { label: "Total Faculty", value: stats?.stats?.total_faculty || 164, sub: "+3 this month" },
    { label: "Fee Collection", value: "₹42.8L", sub: "Collection rate: 87.3%" },
    { label: "Active Courses", value: stats?.stats?.active_courses || 89, sub: "6 semesters" },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} data-testid="admin-dashboard" className="space-y-6">
      {/* Header */}
      <div className="border-b border-[#D8D4CB] pb-5">
        <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Administrator</p>
        <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Good morning, {user?.name?.split(" ")[0]}.</h2>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            data-testid={`stat-card-${i}`}
            className="bg-white border border-[#E2DDD4] p-5 group hover:border-[#0C0C10] transition-colors cursor-default"
            style={{ borderLeft: `3px solid ${CARD_ACCENTS[i]}` }}
          >
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-3">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-[2.2rem] text-[#0C0C10] leading-none">{s.value}</p>
            <div className="flex items-center gap-1.5 mt-3">
              <ArrowUpRight className="w-3 h-3 text-[#22C55E]" />
              <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{s.sub}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2DDD4] p-6" data-testid="enrollment-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-1">Enrollment Trend</p>
              <p className="font-['Syne'] font-bold text-sm text-[#0C0C10]">New student admissions</p>
            </div>
            <span className="font-['JetBrains_Mono'] text-xs text-[#22C55E] bg-[#22C55E]/8 border border-[#22C55E]/20 px-2 py-1">+18% YoY</span>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={enrollmentData}>
              <defs>
                <linearGradient id="enGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0C0C10" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#0C0C10" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke="#F0ECE4" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="students" stroke="#0C0C10" strokeWidth={2} fill="url(#enGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-[#E2DDD4] p-6" data-testid="fees-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-1">Fee Collection</p>
              <p className="font-['Syne'] font-bold text-sm text-[#0C0C10]">Monthly (Lakhs ₹)</p>
            </div>
            <span className="font-['JetBrains_Mono'] text-xs text-[#0A7AFF] bg-[#0A7AFF]/8 border border-[#0A7AFF]/20 px-2 py-1">87.3% rate</span>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={feesData}>
              <CartesianGrid strokeDasharray="2 4" stroke="#F0ECE4" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="collected" fill="#0A7AFF" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pending + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E2DDD4] p-6">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Pending Actions</p>
          <div className="space-y-0">
            {[
              { label: "Admissions", count: 143, color: "#FF3636" },
              { label: "Fee Defaulters", count: 38, color: "#F59E0B" },
              { label: "Leave Approvals", count: 7, color: "#8B5CF6" },
              { label: "Result Publishing", count: 3, color: "#0A7AFF" },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between py-3 border-b border-[#F0ECE4] last:border-0">
                <div className="flex items-center gap-2.5">
                  <span className="w-2 h-2 flex-shrink-0" style={{ background: item.color }} />
                  <span className="font-['DM_Sans'] text-sm text-[#0C0C10]">{item.label}</span>
                </div>
                <span className="font-['JetBrains_Mono'] font-bold text-sm text-[#0C0C10]">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-[#E2DDD4] p-6" data-testid="recent-activity">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Recent Activity</p>
          <div className="space-y-4">
            {recentActivities.map((act, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center flex-shrink-0" style={{ background: act.accent + "15", border: `1px solid ${act.accent}30` }}>
                  <act.icon className="w-3.5 h-3.5" style={{ color: act.accent }} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-['DM_Sans'] text-sm text-[#0C0C10]">{act.text}</p>
                  <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#BDBAA8] mt-0.5">{act.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
